import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Result,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import type { SortOrder } from 'antd/lib/table/interface';
import { colors } from 'theme/tokens';
import type { KcDelta, KcMonth, KcRow, KcTotals, StudioKcResponse, StudioMine } from '../contract';
import { getStudioKc, useStudioLoad } from '../api';
import MonthBars, { MonthBarsDatum } from '../charts/month-bars';
import RankBars, { RankBarsRow } from '../charts/rank-bars';
import DeltaCell from '../components/delta-cell';
import MemberCell from '../components/member-cell';
import StatTile from '../components/stat-tile';
import {
  asOfLabel,
  dayMonth,
  displayName,
  formatHours,
  formatKc,
  formatPct,
  formatSigned,
  monthLabel,
  monthNumber,
  prevMonth,
  shortUid,
  utcMonth,
} from '../format';
import {
  EMPTY_TEXT,
  ESTIMATE_TAG,
  KC_MODE_LABEL,
  MANUAL_TAG,
  MEMBER_TYPE_COLOR,
  MEMBER_TYPE_LABEL,
  NEW_TAG,
  monthNoDataNote,
} from '../labels';
import { describeStudioError } from '../studio-error';
import styles from '../styles.module.scss';

/*
 * Tab Kim cương của trang chi tiết Studio (SPEC Studio mục 8.7.7, 8.8; công thức mục 5; chốt C6, C7).
 * Gói A4. Mọi số do S21 `GET /studios/:id/kc` tính sẵn; tab chỉ chọn số nào hiện ở đâu, KHÔNG tự cộng / tính %.
 * - Tháng gốc mặc định = tháng API chọn; tháng UTC hiện tại chưa có dữ liệu (suggestedMonth nhỏ hơn) thì tự lùi
 *   về suggestedMonth một lần khi mở tab (S21 không tự lùi, mục 5.4) và hiện chú thích.
 * - Công tắc "So cùng số ngày" mặc định bật. Ô số, MonthBars, RankBars so với tháng so sánh ĐẦU TIÊN.
 * - Số của MonthBars theo công tắc (N1-11, bảng mục 8.7.7): bật → tháng gốc = cut.baseTotals của cặp đầu, tháng
 *   so sánh = totals (đã cắt), NONE → null; tắt → fullTotals, không "ước tính".
 * - C6: sameDay=1 không so được → delta là object mọi trường null; sameDay=0 không so được → cut = null,
 *   delta = null. Cả hai hiện "—", không hiện "mới".
 * - C7: cột "Đã phân bổ / Chưa phân bổ" (TeamSplitKc) chỉ hiện khi xem theo Nhóm nhảy và có số.
 */

export interface KcTabProps {
  /** Studio đang xem (`/studio/:id`). */
  studioId: number;
  /** Người xem (GET /studios/mine qua useStudioViewer của trang chi tiết); null khi chưa tải xong / lỗi. */
  viewer?: StudioMine | null;
}

type KcBy = 'creator' | 'team';

// ------------------------------------------------------------- chữ của tab ---
const FULL_CAPTION = 'Tổng tháng (không cắt)';
const NOT_COMPARED_TEXT = 'Chưa so được tổng tháng: có tháng chưa đủ dữ liệu';
const NO_DATA_MONTH_TEXT = 'Chưa có dữ liệu';
const CHANNEL_TAG = 'Kênh';
const joinedTag = (day: string) => `Vào Studio ${dayMonth(day)}`;
const BY_OPTIONS: { label: string; value: KcBy }[] = [
  { label: 'Creator', value: 'creator' },
  { label: 'Nhóm nhảy', value: 'team' },
];

/** Số tháng so sánh tối đa (backend: COMPARE_MAX). */
const COMPARE_MAX = 3;
/** Ô "So sánh với": số tháng liệt kê lùi từ tháng UTC hiện tại, và lùi từ tháng gốc. */
const MONTH_OPTIONS_BACK = 24;
const MONTH_OPTIONS_BEFORE_BASE = 12;

// ------------------------------------------------------------------ tiện ích ---

const joinParts = (parts: (string | null | undefined | false)[]) =>
  parts.filter(Boolean).join(' · ');

/** `count` tháng tính từ `from` lùi dần (gồm `from`). */
function monthsBack(from: string, count: number): string[] {
  const out: string[] = [];
  let month = from;
  for (let i = 0; i < count; i += 1) {
    out.push(month);
    month = prevMonth(month);
  }
  return out;
}

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** So hai số cho sorter của antd, `null` luôn xếp cuối dù sắp tăng hay giảm (antd đảo kết quả khi 'descend'). */
function nullsLast(
  a: number | null | undefined,
  b: number | null | undefined,
  order?: SortOrder
): number {
  const hasA = isNum(a);
  const hasB = isNum(b);
  if (!hasA && !hasB) return 0;
  if (!hasA) return order === 'descend' ? -1 : 1;
  if (!hasB) return order === 'descend' ? 1 : -1;
  return (a as number) - (b as number);
}

/** Tên hiện của một dòng: dòng kênh / Nhóm nhảy / "Creator riêng lẻ" dùng nguyên `name` do API đặt. */
function rowTitle(row: KcRow): string {
  if (row.kind === 'CHANNEL' || row.kind === 'TEAM' || row.kind === 'UNASSIGNED') return row.name;
  return displayName({ nickName: row.nickName, name: row.name, uid: row.uid });
}

/** Chênh của một dòng với tháng so sánh `month` (deltas cùng thứ tự với months[1..]). */
function rowDeltaOf(row: KcRow, month: string, index: number) {
  return row.deltas.find((d) => d.month === month) ?? row.deltas[index] ?? null;
}

// --------------------------------------------------- số của cặp tháng đầu ---

/** Số của cặp (tháng gốc, tháng so sánh đầu tiên) cho hàng ô số, theo công tắc (mục 8.7.7). */
interface PairView {
  base: KcMonth;
  compare: KcMonth | null;
  sameDay: boolean;
  /** Tháng gốc cùng cách cắt với tháng so sánh đầu tiên (bật: cut.baseTotals ; tắt: fullTotals). */
  baseTotals: KcTotals | null;
  compareTotals: KcTotals | null;
  /** C6: null khi tắt công tắc mà không so được; bật công tắc mà không so được → mọi trường null. */
  delta: KcMonth['delta'];
  /** Có so được không (để không hiện "mới" khi không so được). */
  compared: boolean;
  label: string;
  baseEstimated: boolean;
  compareEstimated: boolean;
}

function pairView(data: StudioKcResponse): PairView | null {
  const base = data.months[0];
  if (!base) return null;
  const compare = data.months[1] ?? null;
  const cut = compare?.cut ?? null;
  if (data.sameDay) {
    return {
      base,
      compare,
      sameDay: true,
      baseTotals: cut?.baseTotals ?? null,
      compareTotals: compare && cut && cut.mode !== 'NONE' ? compare.totals : null,
      delta: compare?.delta ?? null,
      compared: !!cut && cut.mode !== 'NONE',
      label: cut?.label ?? '',
      baseEstimated: cut?.baseMode === 'ESTIMATE',
      compareEstimated: cut?.mode === 'ESTIMATE',
    };
  }
  return {
    base,
    compare,
    sameDay: false,
    baseTotals: base.fullTotals,
    compareTotals: compare ? compare.fullTotals : null,
    delta: compare?.delta ?? null,
    compared: !!cut && !!compare?.delta,
    label: cut?.label ?? FULL_CAPTION,
    baseEstimated: false,
    compareEstimated: false,
  };
}

/** Số MonthBars theo công tắc — đúng bảng mục 8.7.7 (N1-11). */
function monthBarsData(data: StudioKcResponse): MonthBarsDatum[] {
  const first = data.months[1] ?? null;
  return data.months.map((m, i): MonthBarsDatum => {
    const role = i === 0 ? 'BASE' : 'COMPARE';
    const label = monthLabel(m.month);
    if (!data.sameDay) {
      return { month: m.month, label, value: m.fullTotals.diamonds, role, estimated: false };
    }
    if (i === 0) {
      return {
        month: m.month,
        label,
        value: first?.cut?.baseTotals?.diamonds ?? null,
        role,
        estimated: first?.cut?.baseMode === 'ESTIMATE',
      };
    }
    const cut = m.cut;
    return {
      month: m.month,
      label,
      value: !cut || cut.mode === 'NONE' ? null : m.totals.diamonds,
      role,
      estimated: cut?.mode === 'ESTIMATE',
      // Cột so sánh thứ hai, thứ ba: nhãn của chính cặp đó (mỗi cặp một D riêng).
      subLabel: i >= 2 && cut ? cut.label : undefined,
    };
  });
}

// ---------------------------------------------------------------- ô số ---

/** Số chênh / % trong ô số: mũi tên + dấu +/−, chữ successInk / dangerInk (mục 8.9: không chỉ dựa vào màu). */
const Trend = ({ value, text, isNew }: { value: number | null; text: string; isNew?: boolean }) => {
  if (isNew) {
    return (
      <span style={{ color: colors.successInk }} aria-label="Mới có số">
        <ArrowUpOutlined aria-hidden /> {NEW_TAG}
      </span>
    );
  }
  if (value === null) return <>—</>;
  const color = value > 0 ? colors.successInk : value < 0 ? colors.dangerInk : colors.textSecondary;
  return (
    <span
      style={{ color }}
      aria-label={`${value > 0 ? 'Tăng' : value < 0 ? 'Giảm' : 'Không đổi'} ${text}`}
    >
      {value > 0 && <ArrowUpOutlined aria-hidden />}
      {value < 0 && <ArrowDownOutlined aria-hidden />} {text}
    </span>
  );
};

/** Dòng phụ của ô LIVE: chênh + % so với tháng so sánh đầu tiên, số tháng đó, nhãn cách cắt. */
const LiveCompare = ({
  delta,
  allowNew,
  baseValue,
  compareValue,
  compareMonth,
  unit,
  label,
}: {
  delta: Pick<KcDelta, 'diff' | 'pct'> | null;
  /** Cho hiện "mới" (pct = null, gốc > 0): chỉ khi so được và phía so sánh không ước tính — phía ESTIMATE có số
   *  LIVE = null theo công thức (mục 5.6), không phải "tháng trước không LIVE". */
  allowNew: boolean;
  baseValue: number | null;
  compareValue: number | null;
  compareMonth: string | null;
  unit?: string;
  label: string;
}) => (
  <DeltaCell
    delta={delta}
    baseValue={allowNew ? baseValue : null}
    sub={joinParts([
      compareMonth &&
        `${monthLabel(compareMonth)}: ${formatKc(compareValue)}${
          unit && isNum(compareValue) ? ` ${unit}` : ''
        }`,
      label,
    ])}
  />
);

const KcTiles = ({ data }: { data: StudioKcResponse }) => {
  const v = pairView(data);
  if (!v) return null;
  const baseMonth = monthLabel(v.base.month);
  const compareMonth = v.compare ? v.compare.month : null;

  const baseKc = v.baseTotals?.diamonds ?? null;
  const compareKc = v.compareTotals?.diamonds ?? null;
  const kcDelta = v.delta?.diamonds ?? null;
  const diff = kcDelta?.diff ?? null;
  const pct = kcDelta?.pct ?? null;
  const isNew = v.compared && pct === null && isNum(baseKc) && baseKc > 0;
  const anyEstimated = v.baseEstimated || v.compareEstimated;

  const baseSub = v.sameDay ? v.label : joinParts([FULL_CAPTION, asOfLabel(v.base.asOfDay)]);
  const compareSub = v.sameDay
    ? v.label
    : v.compare
    ? joinParts([FULL_CAPTION, asOfLabel(v.compare.asOfDay)])
    : '';
  const deltaSub = v.sameDay || v.delta ? v.label : NOT_COMPARED_TEXT;

  // "Creator có LIVE": API không trả chênh cho chỉ số này; chỉ trừ hai số API trả (mục 5.6: chênh = gốc − so sánh).
  const baseLive = v.baseTotals?.creatorsWithLive ?? null;
  const compareLive = v.compareTotals?.creatorsWithLive ?? null;
  const liveCountDelta =
    v.compared && isNum(baseLive) && isNum(compareLive)
      ? { diff: baseLive - compareLive, pct: null }
      : null;

  return (
    <div className={styles.statGrid}>
      <StatTile
        title={`KC tháng gốc · ${baseMonth}`}
        value={formatKc(baseKc)}
        sub={baseSub}
        estimated={v.baseEstimated && baseKc !== null}
      />
      <StatTile
        title={`KC tháng so sánh · ${compareMonth ? monthLabel(compareMonth) : '—'}`}
        value={formatKc(compareKc)}
        sub={compareSub}
        estimated={v.compareEstimated && compareKc !== null}
      />
      <StatTile
        title="Chênh lệch KC"
        value={<Trend value={diff} text={formatSigned(diff)} />}
        sub={deltaSub}
        estimated={anyEstimated && diff !== null}
      />
      <StatTile
        title="% thay đổi"
        value={<Trend value={pct} text={formatPct(pct)} isNew={isNew} />}
        sub={deltaSub}
        estimated={anyEstimated && (pct !== null || isNew)}
      />
      <StatTile
        title={`Giờ LIVE · ${baseMonth}`}
        value={formatKc(v.baseTotals?.liveHours ?? null)}
        sub={
          <LiveCompare
            delta={v.delta?.liveHours ?? null}
            allowNew={v.compared && !v.compareEstimated}
            baseValue={v.baseTotals?.liveHours ?? null}
            compareValue={v.compareTotals?.liveHours ?? null}
            compareMonth={compareMonth}
            unit="giờ"
            label={v.sameDay ? v.label : deltaSub}
          />
        }
      />
      <StatTile
        title={`Ngày LIVE · ${baseMonth}`}
        value={formatKc(v.baseTotals?.liveDays ?? null)}
        sub={
          <LiveCompare
            delta={v.delta?.liveDays ?? null}
            allowNew={v.compared && !v.compareEstimated}
            baseValue={v.baseTotals?.liveDays ?? null}
            compareValue={v.compareTotals?.liveDays ?? null}
            compareMonth={compareMonth}
            unit="ngày"
            label={v.sameDay ? v.label : deltaSub}
          />
        }
      />
      <StatTile
        title={`Creator có LIVE · ${baseMonth}`}
        value={formatKc(baseLive)}
        sub={
          <LiveCompare
            delta={liveCountDelta}
            allowNew={false}
            baseValue={null}
            compareValue={compareLive}
            compareMonth={compareMonth}
            label={v.sameDay ? v.label : deltaSub}
          />
        }
      />
    </div>
  );
};

// ------------------------------------------------------------- biểu đồ ---

function rankSub(row: KcRow, estimated: boolean): string {
  const who =
    row.kind === 'TEAM' || row.kind === 'UNASSIGNED'
      ? row.kcMode
        ? KC_MODE_LABEL[row.kcMode]
        : ''
      : row.team?.name ?? '';
  return joinParts([who, estimated && ESTIMATE_TAG]);
}

const KcCharts = ({ data }: { data: StudioKcResponse }) => {
  const first = data.months[1] ?? null;
  const caption = data.sameDay ? first?.cut?.label ?? '' : FULL_CAPTION;
  const unit = data.by === 'team' ? 'Nhóm nhảy' : 'Creator';
  const firstLabel = first ? monthLabel(first.month) : '—';

  // Top tăng / giảm theo deltas[0].diff (mục 8.7.7); RankBars tự lọc dấu và cắt 5 dòng.
  const rankRows: RankBarsRow[] = [];
  data.rows.forEach((row) => {
    const d = first ? rowDeltaOf(row, first.month, 0) : null;
    if (!d || !isNum(d.diff)) return;
    rankRows.push({
      key: row.key,
      label: rowTitle(row),
      value: d.diff,
      sub: rankSub(row, d.estimated) || undefined,
    });
  });
  const rankCaption = joinParts([
    `So với ${firstLabel}`,
    data.sameDay ? caption : first?.cut?.label,
  ]);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card size="small" title="Kim cương theo tháng" style={{ height: '100%' }}>
          <MonthBars
            data={monthBarsData(data)}
            caption={caption}
            ariaLabel={`Kim cương của Studio theo tháng${caption ? `, ${caption}` : ''}`}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card size="small" title={`${unit} tăng / giảm nhiều nhất`} style={{ height: '100%' }}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {rankCaption && <div className={styles.chartCaption}>{rankCaption}</div>}
            <div className={styles.cellTitle}>Tăng nhiều nhất</div>
            <RankBars
              rows={rankRows}
              mode="UP"
              ariaLabel={`5 ${unit} tăng kim cương nhiều nhất so với ${firstLabel}`}
            />
            <div className={styles.cellTitle}>Giảm nhiều nhất</div>
            <RankBars
              rows={rankRows}
              mode="DOWN"
              ariaLabel={`5 ${unit} giảm kim cương nhiều nhất so với ${firstLabel}`}
            />
          </Space>
        </Card>
      </Col>
    </Row>
  );
};

// ---------------------------------------------------------------- bảng ---

const headTitle = (main: string, sub?: string) => (
  <div>
    <div>{main}</div>
    {sub && (
      <div className={styles.muted} style={{ fontWeight: 400 }}>
        {sub}
      </div>
    )}
  </div>
);

const KcNumber = ({ value, estimated }: { value: number | null; estimated?: boolean }) => {
  if (!isNum(value)) return <>—</>;
  return (
    <Space size={6} wrap={false}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatKc(value)}</span>
      {estimated && <span className={styles.estimateTag}>{ESTIMATE_TAG}</span>}
    </Space>
  );
};

/** Ô tên của dòng Creator (mục 8.7.7: thẻ Vào Studio, Kênh, Quản lý, nhập tay, Không còn trong file TikTok). */
const CreatorName = ({ row }: { row: KcRow }) => {
  const channel = row.kind === 'CHANNEL';
  const handle =
    !channel && row.nickName && row.name && row.name !== row.nickName && row.name !== row.uid
      ? `@${row.name}`
      : '';
  const sub = joinParts([handle, row.uid ? `UID ${shortUid(row.uid)}` : '']);
  const tags = (
    <>
      {row.joinedInRange && row.joinedStudioAt && (
        <Tag color="blue">{joinedTag(row.joinedStudioAt)}</Tag>
      )}
      {channel && <Tag color="purple">{CHANNEL_TAG}</Tag>}
      {row.kind === 'MANAGER' && (
        <Tag color={MEMBER_TYPE_COLOR.MANAGER}>{MEMBER_TYPE_LABEL.MANAGER}</Tag>
      )}
      {row.values[0]?.manual && <span className={styles.estimateTag}>{MANUAL_TAG}</span>}
    </>
  );
  const hasTags =
    (row.joinedInRange && !!row.joinedStudioAt) ||
    channel ||
    row.kind === 'MANAGER' ||
    !!row.values[0]?.manual;
  return (
    <MemberCell
      nickName={channel ? row.name : row.nickName}
      name={row.name}
      uid={row.uid}
      avatar={row.avatar}
      inactive={row.inactive}
      sub={sub || null}
      extra={hasTags ? tags : undefined}
      size={32}
    />
  );
};

/** Ô tên của dòng Nhóm nhảy / "Creator riêng lẻ". */
const TeamName = ({ row }: { row: KcRow }) => {
  const count = row.memberCount ?? 0;
  const sub =
    row.kind === 'UNASSIGNED'
      ? `${count} UID`
      : joinParts([
          `${count} thành viên`,
          row.channelUid ? `Kênh UID ${shortUid(row.channelUid)}` : '',
        ]);
  const manual = !!row.values[0]?.manual;
  return (
    <div className={styles.personText}>
      <span className={styles.personName}>{row.name}</span>
      <span className={styles.personSub}>{sub}</span>
      {(row.kcMode || manual) && (
        <span className={styles.tags}>
          {row.kcMode && (
            <Tag color={row.kcMode === 'CHANNEL' ? 'purple' : 'default'}>
              {KC_MODE_LABEL[row.kcMode]}
            </Tag>
          )}
          {manual && <span className={styles.estimateTag}>{MANUAL_TAG}</span>}
        </span>
      )}
    </div>
  );
};

type KcColumn = ColumnsType<KcRow>[number];

const KcTable = ({ data, loading }: { data: StudioKcResponse; loading: boolean }) => {
  const { columns, summary, width } = useMemo(() => {
    const cols: { column: KcColumn; summary: ReactNode; width: number }[] = [];
    const byTeam = data.by === 'team';
    const base = data.months[0];
    const numberSort = { sortDirections: ['descend', 'ascend'] as SortOrder[] };

    cols.push({
      width: 260,
      column: {
        key: 'name',
        title: byTeam ? 'Nhóm nhảy' : 'Creator',
        fixed: 'left',
        width: 260,
        render: (_: unknown, row: KcRow) =>
          byTeam ? <TeamName row={row} /> : <CreatorName row={row} />,
      },
      summary: (
        <div className={styles.personText}>
          <span className={styles.personName}>Tổng Studio</span>
          {base && <span className={styles.personSub}>{`${base.totals.uids} UID`}</span>}
        </div>
      ),
    });

    if (!byTeam) {
      cols.push({
        width: 150,
        column: {
          key: 'team',
          title: 'Nhóm nhảy',
          width: 150,
          render: (_: unknown, row: KcRow) => row.team?.name ?? '—',
        },
        summary: null,
      });
    }

    // Một cột cho mỗi tháng: tiêu đề MM/YYYY + cut.label; ô "—" khi present = false.
    data.months.forEach((m, i) => {
      const monthSub = !m.hasData
        ? NO_DATA_MONTH_TEXT
        : i === 0
        ? joinParts(['Tháng gốc', asOfLabel(m.asOfDay)])
        : data.sameDay
        ? m.cut?.label ?? ''
        : m.cut?.label ?? joinParts([FULL_CAPTION, asOfLabel(m.asOfDay)]);
      const valueOf = (row: KcRow) => {
        const v = row.values[i];
        return v && v.present ? v.diamonds : null;
      };
      cols.push({
        width: 170,
        column: {
          key: `m:${m.month}`,
          title: headTitle(monthLabel(m.month), monthSub),
          width: 170,
          align: 'right',
          sorter: (a: KcRow, b: KcRow, order?: SortOrder) =>
            nullsLast(valueOf(a), valueOf(b), order),
          ...numberSort,
          render: (_: unknown, row: KcRow) => (
            <KcNumber value={valueOf(row)} estimated={!!row.values[i]?.estimated} />
          ),
        },
        summary: (
          <KcNumber
            value={m.totals.diamonds}
            estimated={data.sameDay && i > 0 && m.cut?.mode === 'ESTIMATE'}
          />
        ),
      });
    });

    // Chênh, % cho mỗi tháng so sánh; sắp theo %, mặc định % của cột chênh đầu tiên giảm dần.
    // Tắt công tắc: cặp không so được (C6: delta = null) không có cột chênh (mục 5.4, 8.7.7); lý do ở notes.
    // Bật công tắc: cột tháng gốc là tổng không cắt, nên ô chênh ghi thêm hai số đã cắt tại D của cặp
    // (deltas[j].baseDiamonds / compareDiamonds; dòng tổng: cut.baseTotals / totals) — tránh đọc "1.500 so 800".
    let sortSet = false;
    data.months.slice(1).forEach((m, j) => {
      if (!data.sameDay && !m.delta) return;
      const pctOf = (row: KcRow) => rowDeltaOf(row, m.month, j)?.pct ?? null;
      const compared = data.sameDay ? !!m.cut && m.cut.mode !== 'NONE' : true;
      const estimated =
        data.sameDay && (m.cut?.mode === 'ESTIMATE' || m.cut?.baseMode === 'ESTIMATE');
      const pairSub = (baseKc: number | null | undefined, compareKc: number | null | undefined) =>
        data.sameDay && compared && (isNum(baseKc) || isNum(compareKc))
          ? `${formatKc(baseKc)} so với ${formatKc(compareKc)}`
          : undefined;
      const defaultSortOrder: SortOrder | undefined = sortSet ? undefined : 'descend';
      sortSet = true;
      cols.push({
        width: 210,
        column: {
          key: `d:${m.month}`,
          title: headTitle(
            `Chênh so với ${monthLabel(m.month)}`,
            data.sameDay ? m.cut?.label : undefined
          ),
          width: 210,
          sorter: (a: KcRow, b: KcRow, order?: SortOrder) => nullsLast(pctOf(a), pctOf(b), order),
          defaultSortOrder,
          ...numberSort,
          render: (_: unknown, row: KcRow) => {
            const d = rowDeltaOf(row, m.month, j);
            if (!d) return '—';
            return (
              <DeltaCell
                delta={d}
                estimated={d.estimated}
                baseValue={d.baseDiamonds}
                sub={pairSub(d.baseDiamonds, d.compareDiamonds)}
              />
            );
          },
        },
        summary: (
          <DeltaCell
            delta={m.delta?.diamonds ?? null}
            estimated={estimated && isNum(m.delta?.diamonds.diff)}
            baseValue={compared ? m.cut?.baseTotals?.diamonds ?? null : null}
            sub={pairSub(m.cut?.baseTotals?.diamonds, m.totals.diamonds)}
          />
        ),
      });
    });

    // Giờ LIVE, Ngày LIVE của tháng gốc; dòng tổng lấy liveHours do API trả (không cộng các ô đã làm tròn).
    cols.push({
      width: 110,
      column: {
        key: 'liveHours',
        title: headTitle('Giờ LIVE', 'tháng gốc'),
        width: 110,
        align: 'right',
        sorter: (a: KcRow, b: KcRow, order?: SortOrder) =>
          nullsLast(a.values[0]?.liveSeconds, b.values[0]?.liveSeconds, order),
        ...numberSort,
        render: (_: unknown, row: KcRow) => formatHours(row.values[0]?.liveSeconds ?? null),
      },
      summary: formatKc(base?.totals.liveHours ?? null),
    });
    cols.push({
      width: 110,
      column: {
        key: 'liveDays',
        title: headTitle('Ngày LIVE', 'tháng gốc'),
        width: 110,
        align: 'right',
        sorter: (a: KcRow, b: KcRow, order?: SortOrder) =>
          nullsLast(a.values[0]?.liveDays, b.values[0]?.liveDays, order),
        ...numberSort,
        render: (_: unknown, row: KcRow) => formatKc(row.values[0]?.liveDays ?? null),
      },
      summary: formatKc(base?.totals.liveDays ?? null),
    });

    // C7 / TeamSplitKc: chỉ khi xem theo Nhóm nhảy và có số (bảng chia của phiên Nhập dữ liệu đã có).
    const hasSplit =
      byTeam &&
      data.rows.some(
        (r) => isNum(r.values[0]?.allocatedDiamonds) || isNum(r.values[0]?.unallocatedDiamonds)
      );
    if (hasSplit) {
      const splitSub = joinParts(['KC kênh', base ? monthLabel(base.month) : '']);
      cols.push({
        width: 140,
        column: {
          key: 'allocated',
          title: headTitle('Đã phân bổ', splitSub),
          width: 140,
          align: 'right',
          sorter: (a: KcRow, b: KcRow, order?: SortOrder) =>
            nullsLast(a.values[0]?.allocatedDiamonds, b.values[0]?.allocatedDiamonds, order),
          ...numberSort,
          render: (_: unknown, row: KcRow) => formatKc(row.values[0]?.allocatedDiamonds ?? null),
        },
        summary: null,
      });
      cols.push({
        width: 140,
        column: {
          key: 'unallocated',
          title: headTitle('Chưa phân bổ', splitSub),
          width: 140,
          align: 'right',
          sorter: (a: KcRow, b: KcRow, order?: SortOrder) =>
            nullsLast(a.values[0]?.unallocatedDiamonds, b.values[0]?.unallocatedDiamonds, order),
          ...numberSort,
          render: (_: unknown, row: KcRow) => formatKc(row.values[0]?.unallocatedDiamonds ?? null),
        },
        summary: null,
      });
    }

    return {
      columns: cols.map((c) => c.column),
      summary: cols.map((c) => c.summary),
      width: cols.reduce((sum, c) => sum + c.width, 0),
    };
  }, [data]);

  return (
    <div className="custom-table">
      <Table<KcRow>
        rowKey="key"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={data.rows}
        pagination={false}
        scroll={{ x: width }}
        locale={{ emptyText: EMPTY_TEXT.kc }}
        // Dòng tổng lấy từ months[i].totals / cut.baseTotals do API trả, KHÔNG tự cộng các ô (mục 8.7.7).
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row>
              {summary.map((cell, index) => (
                <Table.Summary.Cell
                  key={columns[index]?.key ?? index}
                  index={index}
                  align={(columns[index] as { align?: 'right' })?.align}
                >
                  {cell}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

// ----------------------------------------------------------------- tab ---

const KcTab: FC<KcTabProps> = ({ studioId }) => {
  // null = để API chọn (tháng gốc: tháng UTC hiện tại ; so sánh: tháng liền trước).
  const [base, setBase] = useState<string | null>(null);
  const [compare, setCompare] = useState<string[] | null>(null);
  const [by, setBy] = useState<KcBy>('creator');
  const [sameDay, setSameDay] = useState(true);
  /** Tháng UTC hiện tại theo máy chủ (tháng gốc của lần gọi đầu, không gửi base). */
  const [serverMonth, setServerMonth] = useState<string | null>(null);
  /** Tháng UTC hiện tại chưa có dữ liệu → đã tự lùi về suggestedMonth. */
  const [noDataMonth, setNoDataMonth] = useState<string | null>(null);
  /** Tháng đã tự lùi tới; null sau khi người xem tự chọn tháng gốc. */
  const [shiftTarget, setShiftTarget] = useState<string | null>(null);
  const firstChecked = useRef(false);

  const kc = useStudioLoad<StudioKcResponse>(
    () =>
      getStudioKc(studioId, {
        base: base ?? undefined,
        compare: compare ?? undefined,
        by,
        sameDay,
      }),
    [studioId, base, compare, by, sameDay],
    { resetKey: studioId }
  );
  const data = kc.data;

  // Lần trả đầu tiên (không gửi base): nhớ tháng UTC hiện tại của máy chủ; tháng đó chưa có dữ liệu (suggestedMonth
  // nhỏ hơn) thì lùi một lần về suggestedMonth, so với tháng liền trước (mục 8.7.7, 5.4 — chỉ lùi).
  useEffect(() => {
    if (firstChecked.current || base !== null || !data) return;
    firstChecked.current = true;
    const current = data.months[0]?.month;
    if (!current) return;
    setServerMonth(current);
    const suggested = data.suggestedMonth;
    if (suggested && suggested < current) {
      setNoDataMonth(current);
      setShiftTarget(suggested);
      setBase(suggested);
      setCompare([prevMonth(suggested)]);
    }
  }, [data, base]);

  // Đang tự lùi: từ lúc nhận phản hồi của tháng rỗng tới khi có phản hồi của tháng đã lùi. useStudioLoad giữ dữ
  // liệu cũ khi đổi tham số, nên trong khoảng đó KHÔNG vẽ số của tháng rỗng (mục 8.7.7: một lần mở tab là ra số
  // so sánh dùng được); lần gọi sau lỗi thì chỉ còn Alert lỗi + "Thử lại".
  const shifting =
    (base === null &&
      !firstChecked.current &&
      !!data?.suggestedMonth &&
      !!data.months[0] &&
      data.suggestedMonth < data.months[0].month) ||
    (shiftTarget !== null && !!data && data.months[0]?.month !== shiftTarget);

  const effectiveBase = base ?? data?.months[0]?.month ?? null;
  const effectiveCompare = useMemo(
    () => compare ?? (data ? data.months.slice(1).map((m) => m.month) : []),
    [compare, data]
  );
  const maxMonth = serverMonth ?? utcMonth();

  const compareOptions = useMemo(() => {
    const set = new Set<string>(monthsBack(maxMonth, MONTH_OPTIONS_BACK));
    if (effectiveBase) {
      monthsBack(prevMonth(effectiveBase), MONTH_OPTIONS_BEFORE_BASE).forEach((m) => set.add(m));
    }
    effectiveCompare.forEach((m) => set.add(m));
    return Array.from(set)
      .filter((m) => m !== effectiveBase)
      .sort((a, b) => b.localeCompare(a))
      .map((m) => ({ value: m, label: monthLabel(m) }));
  }, [maxMonth, effectiveBase, effectiveCompare]);

  const onBaseChange = (next: string) => {
    if (!next || next === effectiveBase) return;
    // Đang so với tháng liền trước (mặc định) thì dời theo; đã tự chọn thì giữ, chỉ bỏ tháng trùng tháng gốc.
    const wasDefault =
      !!effectiveBase &&
      effectiveCompare.length === 1 &&
      effectiveCompare[0] === prevMonth(effectiveBase);
    let nextCompare = wasDefault ? [prevMonth(next)] : effectiveCompare.filter((m) => m !== next);
    if (!nextCompare.length) nextCompare = [prevMonth(next)];
    setShiftTarget(null);
    setBase(next);
    setCompare(nextCompare);
  };

  const onCompareChange = (next: string[]) => {
    const clean = next.filter((m) => m !== effectiveBase).slice(0, COMPARE_MAX);
    if (!clean.length) return; // luôn giữ ít nhất một tháng so sánh
    setCompare(clean);
  };

  const errorView = kc.error ? describeStudioError(kc.error) : null;
  if (errorView?.kind === 'unavailable') {
    return <Result status="warning" title={errorView.message} />;
  }
  if (errorView?.kind === 'forbidden') {
    return <Result status="403" title="Không xem được kim cương" subTitle={errorView.message} />;
  }

  const firstLoad = !data || shifting;
  const noData = !!data && data.months.every((m) => !m.hasData);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Space size={6}>
            <span className={styles.muted}>Tháng gốc</span>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              allowClear={false}
              aria-label="Tháng gốc"
              disabled={!effectiveBase}
              value={effectiveBase ? dayjs(`${effectiveBase}-01`) : null}
              disabledDate={(date) => date.format('YYYY-MM') > maxMonth}
              onChange={(date) => {
                if (date) onBaseChange(date.format('YYYY-MM'));
              }}
            />
          </Space>
          <Space size={6}>
            <span className={styles.muted}>So sánh với</span>
            <Select
              mode="multiple"
              aria-label="So sánh với"
              style={{ minWidth: 220 }}
              maxCount={COMPARE_MAX}
              disabled={!effectiveBase}
              value={effectiveCompare}
              options={compareOptions}
              onChange={onCompareChange}
              placeholder="Chọn 1–3 tháng"
            />
          </Space>
          <Space size={6}>
            <span className={styles.muted}>Xem theo</span>
            <Segmented options={BY_OPTIONS} value={by} onChange={(value) => setBy(value as KcBy)} />
          </Space>
          <Space size={6}>
            <Switch checked={sameDay} onChange={setSameDay} aria-label="So cùng số ngày" />
            <span>So cùng số ngày</span>
          </Space>
        </div>
      </div>

      {noDataMonth && (
        <div className={styles.muted}>{monthNoDataNote(monthNumber(noDataMonth))}</div>
      )}

      {errorView && (
        <Alert
          type="error"
          showIcon
          message={errorView.message}
          action={
            <Button size="small" onClick={kc.reload}>
              Thử lại
            </Button>
          }
        />
      )}

      {data && !shifting && data.notes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            data.notes.length === 1 ? (
              data.notes[0]
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )
          }
        />
      )}

      {firstLoad ? (
        !errorView && (
          <>
            <div className={styles.statGrid}>
              {[0, 1, 2, 3].map((i) => (
                <StatTile key={i} title="Kim cương" value={null} loading />
              ))}
            </div>
            <Skeleton active paragraph={{ rows: 6 }} />
          </>
        )
      ) : noData ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT.kc} />
      ) : (
        <>
          <KcTiles data={data} />
          <KcCharts data={data} />
          <KcTable data={data} loading={kc.loading} />
        </>
      )}
    </div>
  );
};

export default KcTab;
